import { useEffect } from 'react'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { CheckCircle2, AlertCircle, Download, Clock, XCircle, FileX } from 'lucide-react'

const POLL_INTERVAL_MS = 2000
const POLLING_STATES = new Set(['uploaded', 'previewing', 'committing'])

interface ImportCounts {
  total_rows: number
  valid_count: number
  invalid_count: number
  will_insert: number
  will_update: number
  will_skip_duplicate_in_file: number
  invalid_rows_truncated: boolean
}

interface ImportRowCounts {
  inserted: number
  updated: number
  skipped_invalid: number
  skipped_duplicate_in_file: number
  contacts_created: number
  contacts_linked_existing: number
  affiliations_created: number
  affiliations_ended: number
}

interface ImportHeaderDiff {
  matched_required: string[]
  missing_required: string[]
  extras_ignored: string[]
}

interface ImportSampleRow {
  row_index: number
  organization_name: string
  city: string | null
  governorate: string | null
  email: string | null
  phone: string | null
  contact: { first_name: string; last_name: string } | null
  warnings: string[]
}

interface ImportShape {
  id: number
  data_source: string
  state: 'uploaded' | 'previewing' | 'previewed' | 'committing' | 'confirmed' | 'failed' | 'expired'
  header_diff: ImportHeaderDiff | null
  counts: ImportCounts | null
  sample_rows: ImportSampleRow[]
  row_counts: ImportRowCounts | null
  invalid_count: number
  has_invalid_rows: boolean
  uploaded_at: string | null
  uploaded_by_name: string | null
}

interface ShowProps {
  import: ImportShape
}

const STATE_LABELS: Record<ImportShape['state'], { label: string; icon: typeof CheckCircle2; tone: string }> = {
  uploaded: { label: 'Téléversé', icon: Clock, tone: 'text-muted-foreground' },
  previewing: { label: 'Analyse en cours', icon: Clock, tone: 'text-blue-600' },
  previewed: { label: 'Aperçu prêt', icon: CheckCircle2, tone: 'text-blue-600' },
  committing: { label: 'Import en cours', icon: Clock, tone: 'text-amber-600' },
  confirmed: { label: 'Import terminé', icon: CheckCircle2, tone: 'text-emerald-600' },
  failed: { label: 'Échec', icon: XCircle, tone: 'text-destructive' },
  expired: { label: 'Expiré', icon: FileX, tone: 'text-muted-foreground' },
}

export default function OrganizationDonorImportShow({ import: imp }: ShowProps) {
  const meta = STATE_LABELS[imp.state]
  const StateIcon = meta.icon

  // Auto-poll while the import is in a transitional state (queued job
  // hasn't finished yet). Stops polling as soon as the state lands.
  useEffect(() => {
    if (!POLLING_STATES.has(imp.state)) return
    const id = setInterval(() => {
      router.reload({ only: ['import'], preserveScroll: true, preserveState: true })
    }, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [imp.state])

  function handleConfirm() {
    if (!confirm("Confirmer l'import ? Les lignes valides seront créées ou mises à jour.")) return
    router.post(`/people/imports/organization_donors/${imp.id}/confirm`)
  }

  function handleCancel() {
    if (!confirm("Annuler cet import ? Le fichier sera supprimé.")) return
    router.delete(`/people/imports/organization_donors/${imp.id}`)
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Import #{imp.id} — {imp.data_source}</h1>
          <p className="text-sm text-muted-foreground">
            Téléversé {imp.uploaded_at && `le ${new Date(imp.uploaded_at).toLocaleString('fr-FR')}`}
            {imp.uploaded_by_name && ` par ${imp.uploaded_by_name}`}
          </p>
        </div>
        <div className={`flex items-center gap-2 rounded-md border bg-card px-3 py-2 ${meta.tone}`}>
          <StateIcon className="h-5 w-5" />
          <span className="text-sm font-medium">{meta.label}</span>
        </div>
      </header>

      {imp.header_diff && imp.header_diff.missing_required.length > 0 && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4">
          <p className="flex items-center gap-2 font-medium text-destructive">
            <AlertCircle className="h-5 w-5" />
            En-têtes obligatoires manquants
          </p>
          <ul className="mt-2 list-inside list-disc text-sm text-destructive">
            {imp.header_diff.missing_required.map((h) => <li key={h}>{h}</li>)}
          </ul>
        </div>
      )}

      {POLLING_STATES.has(imp.state) && (
        <div className="flex items-center justify-center gap-3 rounded-md border bg-card p-8">
          <Clock className="h-6 w-6 animate-pulse text-muted-foreground" />
          <div>
            <p className="font-medium">{meta.label}…</p>
            <p className="text-xs text-muted-foreground">Cette page se rafraîchira automatiquement (toutes les 2 secondes).</p>
          </div>
        </div>
      )}
      {imp.state === 'previewed' && imp.counts && <PreviewBody imp={imp} onConfirm={handleConfirm} onCancel={handleCancel} />}
      {imp.state === 'confirmed' && imp.row_counts && <ResultBody imp={imp} />}
      {imp.state === 'failed' && (
        <div className="rounded-md border bg-card p-6 text-center">
          <p className="text-sm text-muted-foreground">L'analyse a échoué. Corrigez le fichier et re-téléversez.</p>
          <Button variant="outline" className="mt-4" onClick={handleCancel}>Supprimer cet import</Button>
        </div>
      )}

      <Link href="/people/imports/organization_donors" className="block text-sm text-muted-foreground hover:underline">
        ← Retour à la liste des imports
      </Link>
    </div>
  )
}

function PreviewBody({ imp, onConfirm, onCancel }: { imp: ImportShape; onConfirm: () => void; onCancel: () => void }) {
  const c = imp.counts!
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card label="Lignes" value={c.total_rows} />
        <Card label="Valides" value={c.valid_count} tone="text-emerald-600" />
        <Card label="Invalides" value={c.invalid_count} tone="text-destructive" />
        <Card label="Doublons" value={c.will_skip_duplicate_in_file} tone="text-amber-600" />
        <Card label="À insérer" value={c.will_insert} tone="text-emerald-600" />
        <Card label="À mettre à jour" value={c.will_update} tone="text-blue-600" />
      </div>

      {imp.sample_rows.length > 0 && (
        <details className="rounded-md border bg-card p-4" open>
          <summary className="cursor-pointer font-medium">Aperçu des {imp.sample_rows.length} premières lignes</summary>
          <table className="mt-3 w-full text-xs">
            <thead className="text-muted-foreground">
              <tr><th className="text-left p-1">#</th><th className="text-left p-1">Nom</th><th className="text-left p-1">Ville</th><th className="text-left p-1">Représentant</th></tr>
            </thead>
            <tbody>
              {imp.sample_rows.map((r) => (
                <tr key={r.row_index} className="border-t">
                  <td className="p-1">{r.row_index}</td>
                  <td className="p-1">{r.organization_name}</td>
                  <td className="p-1">{r.city ?? '—'}</td>
                  <td className="p-1">{r.contact ? `${r.contact.first_name} ${r.contact.last_name}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
        {imp.has_invalid_rows && (
          <a
            href={`/people/imports/organization_donors/${imp.id}/error_report`}
            className="inline-flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm hover:bg-muted"
          >
            <Download className="h-4 w-4" />
            Télécharger les lignes invalides ({imp.invalid_count})
          </a>
        )}
        <Button variant="outline" onClick={onCancel}>Annuler</Button>
        <Button onClick={onConfirm} disabled={c.valid_count === 0}>Confirmer l'import</Button>
      </div>
    </div>
  )
}

function ResultBody({ imp }: { imp: ImportShape }) {
  const r = imp.row_counts!
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
        <p className="flex items-center gap-2 font-medium text-emerald-800">
          <CheckCircle2 className="h-5 w-5" />
          Import terminé
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card label="Créés" value={r.inserted} tone="text-emerald-600" />
        <Card label="Mis à jour" value={r.updated} tone="text-blue-600" />
        <Card label="Contacts créés" value={r.contacts_created} />
        <Card label="Contacts liés" value={r.contacts_linked_existing} />
        <Card label="Affiliations" value={r.affiliations_created} />
        <Card label="Affiliations clôturées" value={r.affiliations_ended} />
        <Card label="Invalides ignorées" value={r.skipped_invalid} tone="text-destructive" />
        <Card label="Doublons ignorés" value={r.skipped_duplicate_in_file} tone="text-amber-600" />
      </div>
      {imp.has_invalid_rows && (
        <a
          href={`/people/imports/organization_donors/${imp.id}/error_report`}
          className="inline-flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm hover:bg-muted"
        >
          <Download className="h-4 w-4" />
          Télécharger les lignes invalides ({imp.invalid_count})
        </a>
      )}
    </div>
  )
}

function Card({ label, value, tone = '' }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-md border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${tone}`}>{value}</p>
    </div>
  )
}
